// frontend/src/components/auth/Login.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SparkLayout from '../layouts/SparkLayout';
import SparkCard from '../common/SparkCard';
import SparkButton from '../common/SparkButton';
import SparkInput from '../common/SparkInput';
import SparkHeading from '../common/SparkHeading';
import SparkFooter from '../common/SparkFooter';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, loginWithGoogle, loading, user, isAnonymous } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Only redirect if user exists AND is not anonymous
    if (user && !isAnonymous) {
      navigate('/dashboard');
    }
  }, [user, isAnonymous, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);
      await login(email, password);
    } catch (err: any) {
      setError(err.message || 'Failed to login.');
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setError(null);
      await loginWithGoogle();
    } catch (err: any) {
      setError(err.message || 'Failed to sign in with Google.');
    }
  };

  if (loading) {
    return (
      <SparkLayout>
        <SparkCard>
          <div className="loading-container flex items-center justify-center h-40">
            <div className="text-center">
              <div className="spark-spinner mx-auto mb-4" />
              <p className="text-xl">Loading...</p>
            </div>
          </div>
        </SparkCard>
      </SparkLayout>
    );
  }

  return (
    <SparkLayout>
      <SparkCard className="max-w-md mx-auto">
        <SparkHeading>Welcome Back</SparkHeading>

        {error && (
          <div className="spark-message spark-message--error mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <SparkInput
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
          />

          <SparkInput
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            required
          />

          <SparkFooter className="flex-col space-y-4">
            <SparkButton
              type="submit"
              fullWidth
              disabled={loading || !email.trim() || !password.trim()}
            >
              {loading ? 'SIGNING IN...' : 'SIGN IN'}
            </SparkButton>

            <SparkButton
              variant="secondary"
              fullWidth
              onClick={handleGoogleLogin}
              disabled={loading}
            >
              SIGN IN WITH GOOGLE
            </SparkButton>
          </SparkFooter>
        </form>

        {/* Updated layout: forgot & register side by side */}
        <div className="mt-8 flex items-center justify-center gap-4 w-full">
          <SparkButton
            variant="secondary"
            size="small"
            to="/forgot-password"
          >
            FORGOT PASSWORD?
          </SparkButton>

          <p className="text-white/80 text-sm text-center m-0">
                Don't have an account?
                <SparkButton
              variant="secondary"
              size="small"
              to="/register"
              className="ml-2 inline-block align-middle bg-transparent border-none shadow-none px-0 py-0 hover:underline"
            >
              REGISTER HERE
            </SparkButton>
          </p>
        </div>
      </SparkCard>
    </SparkLayout>
  );
};

export default Login;